import {getConfig} from "./configMgr";
import {RequestBuilder} from "../queries/request";
import {InvalidSearchParameters} from "../queries/errors";
import {toast} from "react-toastify";
import {extractLabel, getCustomLabel, saveCustomLabel, updateLegacyId, updateLegacyIDs} from "./utils";

// TODO: much of this module could replaced by an OpenAPI/swagger library
const { MAX_URL_LENGTH } = getConfig();

const SEARCH_PAGE = "search";

// Convert URL params into internal JS params which don't fit conversion pattern
// Include _only_ those params used in the URL, _not_ all API params
const API2JS = {
  "ids": "searchIDs",
  "imap": "dsConfig",
  "activity_filter": "xf",  // deprecated alias for xf
};
// Convert from JS to URL param which don't fit conversion pattern
const JS2API = {
  "searchIDs": "ids",
  "searchID": "search_id",
  "queryEntities": "ids",
  "dsConfig": "imap",
  "simTypes": "sim_type",
}
const INTPARAMS = new Set(["neighborhood", "imap", "min_expansion"]);
const FLOATPARAMS = new Set(["sim_threshold"]);
const BOOLPARAMS = new Set(["indirect", "find_related", "sim_substructure", "stream", "density_expansion"]);
// This is probably superfluous, since they're not likely to be included in the queryStr to begin with.
// None of these belongs in a search URL: the result ones have their own endpoints and
// /api/search refuses them (PLEX-6260), limit and labels too, which only evidence chains and
// exports read; the chat ones belong to a chat turn, which GET /api/search (what a short
// search uses) does not take. A URL carrying one would fail.
const IGNOREPARAMS = new Set([
                               "export_format", "datasets_filter", "category", "target_id", "target_category",
                               "limit", "labels",
                               "model", "history_id", "search_id",
                               "agent", "agent_id", "role", "use_tools", "use_remote_tools", "use_markdown",
                               "temperature", "top_p", "top_k", "fork_of", "fork_base", "source", "auto_compact",
                               "user_node_id", "parent_node_id", "resume",
                             ]);

export const convertQueryParamNames = (params) => {
  return Object.entries(params).reduce((result, [key, value]) => {
    if (value === "null") {
      value = null;
    }
    if (JS2API[key]) {
      if (key === "queryEntities" && value) {
        value = value.map(el => el.id);
      }
      key = JS2API[key];
    }
    else {
      key = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
    }
    result[key] = value;
    return result;
  }, {});
};

// fetchParams (optional) — extra options spread into the underlying fetch()
// (e.g. {signal} for AbortController cancellation, PLEX-4234). Defaults to {}
// so existing callers are unaffected.
export const constructSearchAPIRequest = (baseURL, params, forcePOST = false, fetchParams = {}) => {
  const queryParams = convertQueryParamNames(params);
  const url = SearchContext.getAppSearchURL(queryParams, {}, {baseURL});
  if (url === null) {
    throw new InvalidSearchParameters(queryParams);
  }
  if (!forcePOST && url.toString().length < MAX_URL_LENGTH
      && !queryParams.messages
      && !queryParams.templates) {
    return new RequestBuilder(url, fetchParams);
  }
  url.searchParams.delete('ids');
  const body = {
    ids: updateLegacyIDs(queryParams.ids || []),
  }
  if (queryParams.messages) {
    url.searchParams.delete('messages');
    body.messages = queryParams.messages;
  }
  if (queryParams.templates) {
    url.searchParams.delete('templates');
    body.templates = queryParams.templates;
  }
  // v2 project scope. Backend reads these from body.get('program_id'/'project_id')
  // in api.py:search() and threads them into search_context for the coach file
  // tools (list_project_files, read_project_file).
  if (queryParams.program_id) {
    url.searchParams.delete('program_id');
    body.program_id = queryParams.program_id;
  }
  if (queryParams.project_id) {
    url.searchParams.delete('project_id');
    body.project_id = queryParams.project_id;
  }
  return new RequestBuilder(url, fetchParams).asPOST(JSON.stringify(body));
};

class SearchContext {
  // Given parameters in either client or server-side format, generate an appropriate URL
  static getAppSearchURL = (params, localParams = {}, config = {}) => {
    const {baseURL = `/${SEARCH_PAGE}`, withLabels = false} = config;
    if (params?.searchIDs || params?.queryEntities) {
      params = convertQueryParamNames(params);
    }
    if (!params?.ids?.length) {
      if (!params?.messages) {
        return null;
      }
    }
    else {
      // Ensure ids is an array (it might be a string from URL params)
      const idsArray = Array.isArray(params.ids) ? params.ids : [params.ids];
      params.ids = idsArray.map(id => {
        id = updateLegacyId(id);
        if (withLabels) {
          const label = getCustomLabel(id);
          if (label) {
            return `${id} label=${label}`;
          }
        }
        return id;
      });
    }
    const url = new URL(baseURL, window.location.origin);
    Object.entries(params).forEach(([name, value]) => {
      // "none" is an LLM-friendly alias for disabled sim_type; normalize to empty string
      if (name === "sim_type" && value === "none") {
        value = "";
      }
      if (value !== null && value !== "null" && value !== false && value !== undefined) {
        if (value && typeof(value) !== 'string' && value.length) {
          value.forEach(element => {
            url.searchParams.append(name, element);
          });
        }
        else {
          url.searchParams.set(name, value);
        }
      }
    });
    const hashParams = new URLSearchParams(localParams);
    if (url.toString().length > MAX_URL_LENGTH) {
      url.searchParams.getAll('ids').forEach(el => hashParams.append('ids', el));
      url.searchParams.delete('ids');
      url.searchParams.getAll('messages').forEach(el => hashParams.append('messages', el));
      url.searchParams.delete('messages');
    }
    url.hash = hashParams.toString();
    return url;
  };

  // Convert query string parameters into internal app parameters; remove any params that are API-only,
  // e.g. exportFormat, targetID, etc.
  static parseQueryStringParams = (queryStr, hash = "") => {
    if (hash.startsWith("#")) {
      hash = hash.substring(1);
    }
    // Excel botches URL fragments, try to fix that up here
    if (hash.indexOf("ids%3D") !== -1) {
      hash = decodeURIComponent(hash);
    }
    const searchParams = new URLSearchParams(`${queryStr}&${hash}`);
    const params = { searchIDs: []};
    searchParams.forEach((val, name) => {
      const jsKey = API2JS[name] || name.replace(/([a-z])_(id|[a-z])/g, ($0, $1, $2) => `${$1}${$2.toUpperCase()}`);
      if (name === "ids") {
        const ids = updateLegacyIDs(typeof(val) === "string" ? [val] : val)
          .reduce((result, el) => {
            if (el.trim() !== "" && el.indexOf(":") !== -1) {
              const [id, label] = extractLabel(el);
              if (label) {
                saveCustomLabel(id, label)
              }
              result.push(id);
            }
            return result;
          }, []);
        // Ensure all values are unique and sorted
        params[jsKey] = Array.from(new Set(params[jsKey].concat(ids))).sort();
      }
      else if (INTPARAMS.has(name)) {
        params[jsKey] = parseInt(val, 10);
      }
      else if (BOOLPARAMS.has(name)) {
        params[jsKey] = val === "true" || val === "";
      }
      else if (FLOATPARAMS.has(name)) {
        params[jsKey] = parseFloat(val);
      }
      else if (IGNOREPARAMS.has(name)) {
        // These parameters don't belong in a search URL
      }
      else if (/^[a-z][_a-z]+$/.test(name)) {
        if (val !== "undefined") {
          params[jsKey] = val;
        }
      }
      else {
        toast.warning(`Ignoring invalid parameter "${name}"`)
      }
    });
    return params;
  };
}

export default SearchContext;
